/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */
import { useMerchantApiContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, h, VNode } from "preact";
import { InputWithAddon } from "./InputWithAddon.js";
import { InputProps } from "./useField.js";
import { AmountString } from "@gnu-taler/taler-util";
import { useSessionContext } from "../../context/session.js";

export interface Props<T> extends InputProps<T> {
  expand?: boolean;
  addonAfter?: ComponentChildren;
  children?: ComponentChildren;
  side?: ComponentChildren;
}

export function InputCurrency<T>({
  name,
  readonly,
  label,
  placeholder,
  help,
  tooltip,
  expand,
  addonAfter,
  children,
  side,
}: Props<keyof T>): VNode {
  const { config } = useSessionContext();
  const id = config.currencies[config.currency].num_fractional_input_digits;
  const step = Math.pow(10, -1 * (id ?? 0));
  return (
    <InputWithAddon<T>
      name={name}
      readonly={readonly}
      addonBefore={config.currency}
      side={side}
      label={label}
      placeholder={placeholder}
      help={help}
      tooltip={tooltip}
      addonAfter={addonAfter}
      inputType="decimal"
      expand={expand}
      toStr={(v?: AmountString) => v?.split(":")[1] || ""}
      fromStr={(v: string) => (!v ? undefined : `${config.currency}:${v}`)}
      inputExtra={{ min: 0, step }}
    >
      {children}
    </InputWithAddon>
  );
}
